<template>
  <div id="admin-container">
    <AppTopBar :username="usuarioActual?.username || 'admin'" :show-admin="false" :show-logout="false" />

    <!-- Admin tabs (sub-navegación) -->
    <div class="admin-tabs-bar">
      <button @click="cambiarTab('usuarios')" class="admin-tab" :class="{ active: tab === 'usuarios' }">
        USERS
      </button>
      <button @click="cambiarTab('scraps')" class="admin-tab" :class="{ active: tab === 'scraps' }">
        SCRAPS & SYNC
      </button>
      <button v-if="usuarioRol === 'superadmin'" @click="cambiarTab('superadmin')" class="admin-tab" :class="{ active: tab === 'superadmin' }">
        SUPERADMIN
      </button>
      <button v-if="usuarioRol === 'superadmin'" @click="cambiarTab('correcciones')" class="admin-tab" :class="{ active: tab === 'correcciones' }">
        CORRECCIONES
      </button>
    </div>

    <div class="admin-content">
      <!-- TAB USUARIOS -->
      <AdminUsuarios v-if="tab === 'usuarios'" :usuario-actual="usuarioActual" />

      <!-- TAB SCRAPS -->
      <div v-if="tab === 'scraps'" class="scraps-panel">
        <!-- Stats generales -->
        <div class="stats-grid" v-if="scrapsInfo">
          <div class="stat-card">
            <div class="stat-label">REGISTROS TOTALES</div>
            <div class="stat-value">{{ scrapsInfo.stats.total_registros?.toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">PRODUCTOS ÚNICOS</div>
            <div class="stat-value">{{ scrapsInfo.stats.total_productos?.toLocaleString() }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">ÚLTIMO SCRAP</div>
            <div class="stat-value stat-value-sm">{{ formatDate(scrapsInfo.stats.ultimo_sync) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">PRIMER REGISTRO</div>
            <div class="stat-value stat-value-sm">{{ formatDate(scrapsInfo.stats.primer_registro) }}</div>
          </div>
        </div>

        <!-- Tabla de scraps por dia -->
        <div class="panel-section">
          <h3 class="section-title">HISTORIAL DE SCRAPS <span class="section-meta">últimos 30 días · click en fila para exportar</span></h3>
          <div class="table-wrapper" v-if="scrapsInfo">
            <table class="bb-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th class="th-num">Productos únicos</th>
                  <th class="th-num">Registros</th>
                  <th>Primer scrap</th>
                  <th>Último scrap</th>
                  <th class="th-action"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in scrapsInfo.scraps_por_dia" :key="s.scrap_uuid || s.fecha" @click="abrirExportModal(s)" class="row-clickable" title="Click para exportar este scrape">
                  <td class="td-date">{{ s.fecha }}</td>
                  <td class="td-num td-accent">{{ s.productos_unicos?.toLocaleString() }}</td>
                  <td class="td-num">{{ s.registros?.toLocaleString() }}</td>
                  <td class="td-time">{{ formatTime(s.primer_scrap) }}</td>
                  <td class="td-time">{{ formatTime(s.ultimo_scrap) }}</td>
                  <td class="td-action">EXPORT →</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="loading-msg">Cargando...</p>
        </div>

        <!-- Modal Export -->
        <div v-if="exportModalVisible" class="bb-modal-overlay" @click.self="exportModalVisible = false">
          <div class="bb-modal-export">
            <div class="bb-modal-header">
              <h3>EXPORTAR SCRAPE</h3>
              <button @click="exportModalVisible = false" class="bb-close-btn"><PhX :size="13" weight="bold" /></button>
            </div>
            <div class="bb-modal-body">
              <div class="export-info-block">
                <div class="info-row"><span class="info-label">FECHA</span><span class="info-value">{{ exportScrap?.fecha }}</span></div>
                <div class="info-row"><span class="info-label">PRODUCTOS</span><span class="info-value">{{ exportScrap?.productos_unicos?.toLocaleString() }}</span></div>
                <div class="info-row"><span class="info-label">HORA</span><span class="info-value">{{ formatTime(exportScrap?.primer_scrap) }}</span></div>
              </div>
              <div class="export-buttons">
                <button @click="descargarExport('xlsx')" class="bb-btn bb-btn-excel"><PhChartBar :size="13" weight="bold" /> EXCEL</button>
                <button @click="descargarExport('csv')" class="bb-btn bb-btn-csv"><PhDownload :size="13" weight="bold" /> CSV</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TAB CORRECCIONES -->
      <div v-if="tab === 'correcciones'" class="correcciones-panel">
        <div class="panel-section">
          <h3 class="section-title">PRODUCTOS EN CORRECCIÓN <span class="section-meta">{{ productosCorreccion.length }} productos · ocultos en todos los módulos</span></h3>
          <div class="table-wrapper">
            <table class="bb-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descripción</th>
                  <th>Motivo</th>
                  <th>Marcado por</th>
                  <th>Fecha</th>
                  <th class="th-action"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in productosCorreccion" :key="p.codigo">
                  <td class="td-code">{{ p.codigo }}</td>
                  <td class="td-desc">{{ p.descripcion }}</td>
                  <td class="td-muted">{{ p.motivo || "—" }}</td>
                  <td class="td-muted">{{ p.creado_por }}</td>
                  <td class="td-muted td-time">{{ new Date(p.created_at).toLocaleDateString() }}</td>
                  <td class="td-action-btn">
                    <button @click="quitarCorreccion(p.codigo)" class="bb-btn-inline bb-btn-restore"><PhCheck :size="11" weight="bold" /> RESTABLECER</button>
                  </td>
                </tr>
                <tr v-if="productosCorreccion.length === 0">
                  <td colspan="6" class="empty-row">Sin productos en corrección</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- TAB SUPERADMIN -->
      <div v-if="tab === 'superadmin'" class="superadmin-panel">
        <!-- Configuración -->
        <div class="panel-section">
          <h3 class="section-title">CONFIGURACIÓN DEL SISTEMA</h3>
          <div class="config-list">
            <div class="config-row">
              <label class="config-label">Días de retención de scraps</label>
              <input v-model.number="retencionDias" type="number" min="7" max="365" class="bb-input" />
              <button @click="guardarRetencion" class="bb-btn-inline bb-btn-save">GUARDAR</button>
            </div>
            <div class="config-row">
              <label class="config-label">Max % Oportunidades de Margen</label>
              <input v-model.number="maxPctOportunidades" type="number" min="10" max="1000" class="bb-input" />
              <button @click="guardarConfig('max_pct_oportunidades', maxPctOportunidades)" class="bb-btn-inline bb-btn-save">GUARDAR</button>
            </div>
            <div class="config-row">
              <label class="config-label">Max % Productos con Precio Alto</label>
              <input v-model.number="maxPctPreciosAltos" type="number" min="10" max="1000" class="bb-input" />
              <button @click="guardarConfig('max_pct_precios_altos', maxPctPreciosAltos)" class="bb-btn-inline bb-btn-save">GUARDAR</button>
            </div>
          </div>
        </div>

        <!-- Borrar scraps -->
        <div class="panel-section">
          <h3 class="section-title">SCRAPS EN DB LOCAL <span class="section-meta">acción destructiva</span></h3>
          <div class="table-wrapper">
            <table class="bb-table">
              <thead>
                <tr>
                  <th>UUID</th>
                  <th>Fecha</th>
                  <th class="th-num">Registros</th>
                  <th class="th-action"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in scrapsInfo?.scraps_por_dia || []" :key="s.fecha">
                  <td class="td-uuid">{{ s.scrap_uuid || "—" }}</td>
                  <td class="td-date">{{ s.fecha }}</td>
                  <td class="td-num">{{ s.registros?.toLocaleString() }}</td>
                  <td class="td-action-btn">
                    <button @click="borrarScrap(s.scrap_uuid, s.fecha)" class="bb-btn-inline bb-btn-delete">BORRAR</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import AppTopBar from "../components/AppTopBar.vue";
import axios from "axios";
import AdminUsuarios from "../components/AdminUsuarios.vue";
import { PhX, PhChartBar, PhDownload, PhCheck } from '@phosphor-icons/vue'

const API = "https://dash-visaovip.kompara.tech/api";
const getH = () => ({ headers: { Authorization: "Bearer " + localStorage.getItem("token_precios") } });

const tab = ref(localStorage.getItem("admin_tab") || "usuarios");
const usuarioRol = computed(() => {
  const token = localStorage.getItem("token_precios");
  if (!token) return "";
  try { return JSON.parse(atob(token.split(".")[1])).rol || ""; }
  catch { return ""; }
});
const cambiarTab = (t) => {
  tab.value = t;
  localStorage.setItem("admin_tab", t);
};
const scrapsInfo = ref(null);
const exportModalVisible = ref(false);
const exportScrap = ref(null);

const abrirExportModal = (s) => {
  if (!s.scrap_uuid) {
    alert("Este scrape no tiene UUID disponible.");
    return;
  }
  exportScrap.value = s;
  exportModalVisible.value = true;
};

const descargarExport = (formato) => {
  const uuid = exportScrap.value.scrap_uuid;
  const token = localStorage.getItem("token_precios");
  const url = `${API}/exportar/scrape/${uuid}?formato=${formato}`;
  fetch(url, { headers: { Authorization: "Bearer " + token } })
    .then(r => {
      if (!r.ok) throw new Error(r.statusText);
      const cd = r.headers.get("content-disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `scrape.${formato}`;
      return r.blob().then(b => ({ blob: b, filename }));
    })
    .then(({ blob, filename }) => {
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      exportModalVisible.value = false;
    })
    .catch(err => {
      alert("Error al descargar: " + err.message);
    });
};

const usuarioActual = computed(() => {
  const token = localStorage.getItem("token_precios");
  if (!token) return "";
  try { return JSON.parse(atob(token.split(".")[1])).sub || ""; }
  catch { return ""; }
});

const formatDate = (dt) => {
  if (!dt) return "-";
  return new Date(dt).toLocaleString("es-PY");
};

const formatTime = (dt) => {
  if (!dt) return "-";
  return new Date(dt).toLocaleTimeString("es-PY");
};

const loadScrapsInfo = async () => {
  const res = await axios.get(API + "/usuarios/admin/scraps-info", getH());
  scrapsInfo.value = res.data;
};

const retencionDias = ref(60);
const maxPctOportunidades = ref(200);
const maxPctPreciosAltos = ref(200);
const productosCorreccion = ref([]);

const cargarCorreccion = async () => {
  const res = await axios.get(API + "/usuarios/superadmin/productos-correccion", getH());
  productosCorreccion.value = res.data;
};

const quitarCorreccion = async (codigo) => {
  await axios.delete(API + "/usuarios/superadmin/productos-correccion/" + codigo, getH());
  await cargarCorreccion();
};

const guardarConfig = async (clave, valorRef) => {
  try {
    const v = (valorRef !== null && typeof valorRef === "object") ? valorRef.value : valorRef;
    await axios.post(API + "/usuarios/superadmin/config?clave=" + clave + "&valor=" + v, {}, getH());
    alert("Configuración guardada");
  } catch(e) { alert("Error al guardar"); }
};

const guardarRetencion = async () => {
  await axios.post(API + "/usuarios/superadmin/config?clave=retencion_dias&valor=" + retencionDias.value, {}, getH());
  alert("Retención actualizada a " + retencionDias.value + " días");
};

const borrarScrap = async (scrapUuid, fecha) => {
  if (!scrapUuid) { alert("UUID no disponible para este scrap"); return; }
  if (!confirm("Borrar scrap del " + fecha + "? Esta acción no se puede deshacer.")) return;
  try {
    const res = await axios.delete(API + "/usuarios/superadmin/scraps?scrap_uuid=" + scrapUuid, getH());
    alert("Eliminados " + res.data.eliminados + " registros");
    await loadScrapsInfo();
  } catch (e) {
    alert("Error al borrar scrap");
  }
};

watch(tab, async (newTab) => {
  if (newTab === "superadmin") {
    if (!scrapsInfo.value) await loadScrapsInfo();
    try {
      const cfgRes = await axios.get(API + "/usuarios/superadmin/config", getH());
      if (cfgRes.data.retencion_dias) retencionDias.value = parseInt(cfgRes.data.retencion_dias);
      if (cfgRes.data.max_pct_oportunidades) maxPctOportunidades.value = parseInt(cfgRes.data.max_pct_oportunidades);
      if (cfgRes.data.max_pct_precios_altos) maxPctPreciosAltos.value = parseInt(cfgRes.data.max_pct_precios_altos);
    } catch(e) { console.error(e); }
  }
  if (newTab === "scraps") await loadScrapsInfo();
  if (newTab === "correcciones") await cargarCorreccion();
});

onMounted(async () => {
  await loadScrapsInfo();
  if (usuarioRol.value === "superadmin") {
    try {
      const cfgRes = await axios.get(API + "/usuarios/superadmin/config", getH());
      if (cfgRes.data.retencion_dias) retencionDias.value = parseInt(cfgRes.data.retencion_dias);
      if (cfgRes.data.max_pct_oportunidades) maxPctOportunidades.value = parseInt(cfgRes.data.max_pct_oportunidades);
      if (cfgRes.data.max_pct_precios_altos) maxPctPreciosAltos.value = parseInt(cfgRes.data.max_pct_precios_altos);
    } catch(e) { console.error(e); }
  }
});
</script>

<style scoped>
#admin-container {
  min-height: 100vh;
  background: var(--color-background);
  color: var(--color-text);
}

/* ============ ADMIN SUB-TABS ============ */
.admin-tabs-bar {
  display: flex;
  gap: 0;
  background: var(--color-surface-2);
  border-bottom: 1px solid var(--color-border);
  padding: 0 16px;
}
.admin-tab {
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 9px 16px;
  color: var(--color-text-muted);
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  transition: all 0.15s;
}
.admin-tab:hover {
  color: var(--color-text);
  background: var(--color-hover-bg);
}
.admin-tab.active {
  color: var(--color-accent);
  background: var(--color-accent-bg);
  border-bottom-color: var(--color-accent);
}

/* ============ CONTENT ============ */
.admin-content {
  padding: 20px 24px;
  max-width: 1400px;
  margin: 0 auto;
}

/* ============ STATS GRID ============ */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: var(--color-border);
  gap: 1px;
  margin-bottom: 20px;
  border: 1px solid var(--color-border);
  border-radius: 3px;
  overflow: hidden;
}
.stat-card {
  background: var(--color-surface);
  padding: 14px 16px;
}
.stat-label {
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-text-muted);
  margin-bottom: 6px;
}
.stat-value {
  font-family: var(--font-mono);
  font-size: 22px;
  font-weight: 700;
  color: var(--color-text);
  font-variant-numeric: tabular-nums;
  line-height: 1;
  letter-spacing: -0.01em;
}
.stat-value-sm {
  font-size: 12.5px;
  color: var(--color-accent);
  font-weight: 600;
}

/* ============ PANEL SECTIONS ============ */
.panel-section {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  margin-bottom: 16px;
  overflow: hidden;
}
.section-title {
  margin: 0;
  padding: 10px 16px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-accent);
  text-transform: uppercase;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
  display: flex;
  align-items: center;
  gap: 10px;
}
.section-meta {
  font-family: var(--font-mono);
  font-size: 9.5px;
  color: var(--color-text-faint);
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: none;
}
.loading-msg {
  padding: 16px;
  text-align: center;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: 11px;
  font-style: italic;
}

/* ============ TABLA Bloomberg ============ */
.table-wrapper {
  overflow-x: auto;
}
.bb-table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-mono);
}
.bb-table thead th {
  text-align: left;
  padding: 7px 12px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-faint);
  border-bottom: 1px solid var(--color-border-strong);
  background: var(--color-surface-2);
  white-space: nowrap;
}
.bb-table thead .th-num { text-align: right; }
.bb-table thead .th-action { text-align: right; width: 140px; }
.bb-table tbody td {
  padding: 7px 12px;
  font-size: 11.5px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
  font-variant-numeric: tabular-nums;
}
.bb-table tbody tr:last-child td {
  border-bottom: none;
}
.row-clickable {
  cursor: pointer;
  transition: background 0.1s;
}
.row-clickable:hover {
  background: var(--color-hover-row);
}
.row-clickable:hover .td-action {
  color: var(--color-accent);
}

/* Celdas específicas */
.td-date {
  color: var(--color-text);
  font-weight: 600;
}
.td-num { text-align: right; }
.td-accent {
  color: var(--color-accent);
  font-weight: 700;
}
.td-time {
  color: var(--color-text-muted);
  font-size: 10.5px;
}
.td-action {
  text-align: right;
  color: var(--color-text-faint);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  transition: color 0.1s;
}
.td-action-btn {
  text-align: right;
}
.td-code {
  color: var(--color-text);
  font-weight: 600;
}
.td-desc {
  font-family: 'Inter', sans-serif;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.td-muted {
  color: var(--color-text-muted);
  font-size: 10.5px;
}
.td-uuid {
  color: var(--color-text-faint);
  font-size: 10px;
}
.empty-row {
  padding: 20px !important;
  text-align: center;
  color: var(--color-text-faint);
  font-style: italic;
}

/* ============ BOTONES INLINE ============ */
.bb-btn-inline {
  background: transparent;
  border: 1px solid var(--color-border-strong);
  color: var(--color-text-muted);
  padding: 4px 10px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: all 0.15s;
}
.bb-btn-save:hover {
  color: var(--color-success);
  border-color: var(--color-success);
}
.bb-btn-restore {
  color: var(--color-success);
  border-color: var(--color-success);
}
.bb-btn-restore:hover {
  background: var(--color-success-bg);
}
.bb-btn-delete {
  color: var(--color-danger);
  border-color: var(--color-danger);
}
.bb-btn-delete:hover {
  background: var(--color-danger-bg);
}

/* ============ CONFIG SECTION ============ */
.config-list {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.config-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  border-radius: 2px;
}
.config-label {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: var(--color-text);
  font-weight: 500;
}
.bb-input {
  width: 90px;
  padding: 5px 8px;
  border-radius: 2px;
  border: 1px solid var(--color-border-strong);
  background: var(--color-background);
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  text-align: right;
  transition: border-color 0.15s;
}
.bb-input:focus {
  outline: none;
  border-color: var(--color-accent);
}

/* ============ MODAL EXPORT ============ */
.bb-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.bb-modal-export {
  background: var(--color-surface);
  border: 1px solid var(--color-accent-border);
  border-radius: 3px;
  min-width: 380px;
  max-width: 500px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}
.bb-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
}
.bb-modal-header h3 {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.bb-close-btn {
  background: transparent;
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-strong);
  padding: 3px 8px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 11px;
  font-family: var(--font-mono);
  transition: all 0.15s;
}
.bb-close-btn:hover {
  color: var(--color-danger);
  border-color: var(--color-danger);
}
.bb-modal-body {
  padding: 16px;
}
.export-info-block {
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  border-radius: 2px;
  padding: 10px 12px;
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: var(--font-mono);
  font-size: 11px;
}
.info-label {
  color: var(--color-text-faint);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
}
.info-value {
  color: var(--color-text);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.export-buttons {
  display: flex;
  gap: 8px;
}
.bb-btn {
  flex: 1;
  padding: 10px 16px;
  border: 1px solid var(--color-border-strong);
  background: transparent;
  color: var(--color-text-muted);
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  transition: all 0.15s;
}
.bb-btn-excel:hover {
  color: var(--color-success);
  border-color: var(--color-success);
  background: var(--color-success-bg);
}
.bb-btn-csv:hover {
  color: var(--color-text);
  border-color: var(--color-text-muted);
  background: var(--color-hover-bg);
}
</style>